"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type EigenCtx,
  EigenResults,
} from "@/components/tools/linear-algebra/eigen-results";
import { MatrixGrid } from "@/components/tools/linear-algebra/matrix-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { solveEigen } from "@/lib/tool-kit/linear-algebra/eigen";
import { parseMatrix } from "@/lib/tool-kit/linear-algebra/matrix";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import type { NumberStyle } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const EIGENVALUE_CALCULATOR_STRINGS = {
  sizeLabel: "Matrix size (n × n)",
  matrixA: "Matrix A",
  cellLabel: "Matrix A, row {row}, column {col}",
  entryHint: "Whole numbers, decimals or fractions such as 3/4.",
  results: "Results",
  enterAll: "Fill every cell of the matrix to see its eigenvalues.",
  invalid:
    "Enter each entry as a number or a fraction, such as 2, −0.5 or 3/4.",
  eigenvaluesLabel:
    "Eigenvalues (repeated according to algebraic multiplicity)",
  copyEigenvalues: "Copy eigenvalues",
  copied: "Copied!",
  traceLabel: "Trace",
  determinantLabel: "Determinant",
  methodLabel: "Method",
  methodExact: "Exact",
  methodNumeric: "Numeric (QR)",
  symmetricLabel: "Symmetric?",
  diagonalizableLabel: "Diagonalizable?",
  yes: "Yes",
  no: "No",
  traceCheck:
    "Check: the eigenvalues add up to the trace, {trace}, and multiply to the determinant, {det}.",
  polynomialHeading: "Characteristic polynomial",
  polyWorking2:
    "p(λ) = det(λI − A) = λ² − tr(A)·λ + det(A) = λ² − ({trace})λ + ({det}) = {poly}",
  polyWorking3:
    "p(λ) = det(λI − A) = λ³ − tr(A)·λ² + c₂·λ − det(A) = λ³ − ({trace})λ² + ({c2})λ − ({det}) = {poly}, where c₂ is the sum of the three principal 2 × 2 minors.",
  polyWorkingN:
    "p(λ) = det(λI − A) = {poly}, computed exactly with the Faddeev–LeVerrier recurrence.",
  factoredLine: "p(λ) = {factored}",
  rationalRoots:
    "Rational root theorem: a rational root is a divisor of the constant term over a divisor of the leading coefficient. Roots found: {roots}.",
  rootTimes: "{root} (× {n})",
  noRationalRoots:
    "No rational roots exist, so no eigenvalue is a whole number or fraction.",
  quadraticStep:
    "The remaining quadratic {quadratic} has discriminant b² − 4ac = {disc}, and the quadratic formula gives its roots exactly.",
  numericReason:
    "The polynomial does not split into rational roots and at most one quadratic factor, so the eigenvalues cannot be written exactly with fractions and square roots here. They are computed numerically instead.",
  eigenvectorsHeading: "Eigenvalues and eigenvectors",
  exactVectorNote:
    "Each eigenvector solves (A − λI)v = 0. Any non-zero multiple of it is also an eigenvector.",
  numericNote:
    "Numeric results from the shifted QR algorithm in double precision. An eigenvalue whose imaginary part is below {tolerance} of its size (relative) is shown as real; eigenvectors are scaled so their largest entry is 1.",
  lambdaIs: "λ = {value}",
  decimalValue: "≈ {value}",
  multiplicities:
    "algebraic multiplicity {algebraic}, geometric multiplicity {geometric}",
  shiftedMatrix: "A − λI",
  eigenvector: "Eigenvector",
  eigenvectorsPlural: "Eigenvectors",
  diagonalizationHeading: "Diagonalization",
  diagonalizationNote:
    "The eigenvectors form the columns of P, and A = P D P⁻¹ with D the diagonal matrix of eigenvalues.",
  defective:
    "A is not diagonalizable: for at least one eigenvalue the eigenspace is smaller than the algebraic multiplicity, so A has too few independent eigenvectors.",
} as const;

const SIZES = [2, 3, 4, 5, 6] as const;
const DECIMAL_INPUT = /[.,]/;

function padded(rows: string[][]): string[][] {
  return Array.from({ length: 6 }, (_, i) =>
    Array.from({ length: 6 }, (_, j) => rows[i]?.[j] ?? "0")
  );
}

const DEFAULT_CELLS = padded([
  ["4", "1"],
  ["2", "3"],
]);

function Outcome({ cells, ctx }: { cells: string[][]; ctx: EigenCtx }) {
  const parsed = parseMatrix(cells);
  if (!parsed.ok) {
    return parsed.reason === "invalid" ? (
      <ErrorNotice>{ctx.strings.invalid}</ErrorNotice>
    ) : (
      <Notice>{ctx.strings.enterAll}</Notice>
    );
  }
  return <EigenResults ctx={ctx} solution={solveEigen(parsed.matrix)} />;
}

export default function EigenvalueCalculatorWidget() {
  const strings = useToolUi(EIGENVALUE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [size, setSize] = useState(2);
  const [cells, setCells] = useState(DEFAULT_CELLS);
  const active = cells.slice(0, size).map((row) => row.slice(0, size));
  const style: NumberStyle = active
    .flat()
    .some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const ctx: EigenCtx = { strings, style, mark: decimalMarkOf(fmt), fmt };
  return (
    <>
      <SelectField
        id="ev-size"
        label={strings.sizeLabel}
        onChange={(value) => setSize(Number(value))}
        options={SIZES.map((n) => ({ value: String(n), label: fmt(n) }))}
        value={String(size)}
      />
      <fieldset className="space-y-3 rounded-lg border border-border p-4">
        <legend className="px-1 font-medium text-foreground text-sm">
          {strings.matrixA}
        </legend>
        <MatrixGrid
          cellLabel={(row, col) =>
            t(strings.cellLabel, { row: fmt(row), col: fmt(col) })
          }
          cells={cells}
          cols={size}
          idPrefix="ev-a"
          onChange={(row, col, text) =>
            setCells((prev) =>
              prev.map((r, i) =>
                i === row ? r.map((c, j) => (j === col ? text : c)) : r
              )
            )
          }
          rows={size}
        />
      </fieldset>
      <p className="text-muted-foreground text-xs">{strings.entryHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome cells={active} ctx={ctx} />
      </section>
    </>
  );
}
